import { useCallback } from 'react';
import type { Router } from '../types';
import { getRedirectPath, clearRedirectPath } from '../utils/redirectStorage';

/**
 * Parameters for useAuthRedirect hook
 */
export interface UseAuthRedirectParams {
    /**
     * Router instance for navigation
     */
    router: Router;
    /**
     * Path to redirect to after authentication.
     * Defaults to '/' for single-tenant applications.
     */
    homePath?: string;
}

/**
 * Result of useAuthRedirect hook
 */
export interface UseAuthRedirectResult {
    /**
     * Callback to handle Auth0 redirect after authentication
     */
    onRedirectCallback: () => void;
}

/**
 * Custom hook to handle Auth0 redirect logic.
 * After authentication, it redirects to the saved redirect path if available,
 * otherwise to the home path.
 *
 * @param params - Configuration for the redirect behavior
 * @returns Object with onRedirectCallback function
 *
 * @example
 * ```tsx
 * const { onRedirectCallback } = useAuthRedirect({
 *   router,
 * });
 *
 * return (
 *   <Auth0Provider onRedirectCallback={onRedirectCallback}>
 *     {children}
 *   </Auth0Provider>
 * );
 * ```
 */
export const useAuthRedirect = ({
    router,
    homePath = '/',
}: UseAuthRedirectParams): UseAuthRedirectResult => {
    const onRedirectCallback = useCallback((): void => {
        // Get the saved redirect path from sessionStorage
        const redirectPath = getRedirectPath();

        // Determine the target path: use saved redirect path if available, otherwise home
        const targetPath = redirectPath ?? homePath;

        // Clear the saved redirect path
        clearRedirectPath();

        // Navigate to the target path
        router.navigate(targetPath, { replace: true });
    }, [router, homePath]);

    return { onRedirectCallback };
};

export default useAuthRedirect;
